/**
 * @since 2026-09-04
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

// The scheduler view keeps its filters in the query string so a refresh, a
// bookmark or a shared link all land on exactly the same view.

const URL_PARAM_KEYS = {
  schedulerSelected: "scheduler",
  statusFilter: "status",
  searchTerm: "search",
  sortField: "sort",
  sortOrder: "order",
};

const LIST_SEPARATOR = ",";

const VALID_STATUSES = ["WAIT", "RUN", "OK", "WARN", "ERR", "HOLD", "SKIP"];

const VALID_SORT_FIELDS = [
  "Status",
  "Name",
  "Scheduler",
  "StartTime",
  "EndTime",
  "Duration",
  "Runs",
];

export const DEFAULT_FILTERS = {
  schedulerSelected: "ALL",
  statusFilter: ["WAIT", "RUN", "WARN", "ERR"],
  searchTerm: "",
  sortField: "Status",
  sortOrder: "asc",
};

// Read the filters from the current URL, falling back to the defaults for any
// parameter that is missing or holds a value we don't recognise.
export const readFiltersFromUrl = () => {
  const filters = { ...DEFAULT_FILTERS };

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return filters;
  }

  const schedulerSelected = params.get(URL_PARAM_KEYS.schedulerSelected);
  if (schedulerSelected) {
    filters.schedulerSelected = schedulerSelected;
  }

  // An empty "status" parameter means every status filter was deselected,
  // which is not the same as the parameter being absent (that one keeps the
  // default selection), so test against null rather than truthiness.
  const statusFilter = params.get(URL_PARAM_KEYS.statusFilter);
  if (statusFilter !== null) {
    filters.statusFilter = statusFilter
      .split(LIST_SEPARATOR)
      .map((status) => status.trim().toUpperCase())
      .filter((status) => VALID_STATUSES.includes(status));
  }

  const searchTerm = params.get(URL_PARAM_KEYS.searchTerm);
  if (searchTerm !== null) {
    filters.searchTerm = searchTerm;
  }

  const sortField = params.get(URL_PARAM_KEYS.sortField);
  if (VALID_SORT_FIELDS.includes(sortField)) {
    filters.sortField = sortField;
  }

  const sortOrder = (params.get(URL_PARAM_KEYS.sortOrder) || "").toLowerCase();
  if (sortOrder === "asc" || sortOrder === "desc") {
    filters.sortOrder = sortOrder;
  }

  return filters;
};

// Mirror the filters into the URL. Values left at their default are dropped so
// the address bar stays readable, and the entry is replaced rather than pushed
// so the back button doesn't have to walk through every filter click.
export const writeFiltersToUrl = (filters) => {
  try {
    const params = new URLSearchParams(window.location.search);

    const applyParam = (key, value, isDefault) => {
      if (isDefault) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    };

    const statusFilter = (filters.statusFilter || []).join(LIST_SEPARATOR);

    applyParam(
      URL_PARAM_KEYS.schedulerSelected,
      filters.schedulerSelected,
      filters.schedulerSelected === DEFAULT_FILTERS.schedulerSelected
    );
    applyParam(
      URL_PARAM_KEYS.statusFilter,
      statusFilter,
      statusFilter === DEFAULT_FILTERS.statusFilter.join(LIST_SEPARATOR)
    );
    applyParam(
      URL_PARAM_KEYS.searchTerm,
      filters.searchTerm,
      filters.searchTerm === DEFAULT_FILTERS.searchTerm
    );
    applyParam(
      URL_PARAM_KEYS.sortField,
      filters.sortField,
      filters.sortField === DEFAULT_FILTERS.sortField
    );
    applyParam(
      URL_PARAM_KEYS.sortOrder,
      filters.sortOrder,
      filters.sortOrder === DEFAULT_FILTERS.sortOrder
    );

    const queryString = params.toString();
    const url =
      window.location.pathname +
      (queryString ? `?${queryString}` : "") +
      window.location.hash;

    window.history.replaceState(window.history.state, "", url);
  } catch {}
};
